import { useEffect, useMemo, useRef, useState } from 'react'
import { useChartSave } from '../lib/chartSave.jsx'
import {
  ScanEye,
  Pencil,
  Trash2,
  Plus,
  Lock,
} from 'lucide-react'
import { AdultToothChart, PedoToothChart } from './ToothChart/index.js'
import { redirectToLogin } from '../lib/api.js'
import { ORAL_EXAM_TYPES, typeMeta } from '../lib/oral-exam.js'
import OralExamChart from './OralExamChart.jsx'
import { Badge, Banner, Button, ChoiceChips, DrawerFooter, ErrorState, Field, IconButton, Input, Modal, Segmented, useToast } from '../kit'

/**
 * Per-tooth findings (oral exam). Lives on chart.oral_exam[]. Each entry:
 *   { tag: '16' | 'Full Mouth' | ['16'], type, detail, user_id, date, timestamp }
 * Stored as array `['16']` on save for forward-compat. Tag may also arrive as a
 * raw string from older shapes — handled at read time.
 *
 * UX:
 *   - Click a tooth on the chart → mini popup opens for that tooth.
 *     Popup shows existing findings on the tooth + a form to add another.
 *     Popup stays open so the user can quickly add multiple findings per tooth.
 *   - Full Mouth findings live in a dedicated section with its own inline form.
 *   - All findings are listed (grouped by tooth) below the chart for review
 *     before save.
 */

const FULL_MOUTH = 'Full Mouth'

// Each finding type's existing colour (TYPE_META `dot` shade in lib/oral-exam.js)
// as a CSS colour for the kit's Badge / ChoiceChips. Unknown types fall back to
// อื่นๆ (slate), exactly as typeMeta() does.
const TYPE_DOT_HEX = {
  'bg-rose-500': '#f43f5e', 'bg-red-500': '#ef4444', 'bg-fuchsia-500': '#d946ef',
  'bg-orange-500': '#f97316', 'bg-teal-500': '#14b8a6', 'bg-yellow-500': '#eab308',
  'bg-violet-500': '#8b5cf6', 'bg-indigo-500': '#6366f1', 'bg-slate-400': '#94a3b8',
}
const typeColor = (t) => TYPE_DOT_HEX[typeMeta(t).dot] || '#94a3b8'

export default function OralExamEditor({ chart, canEdit, lockNote, onSaved }) {
  const [mode, setMode]   = useState('view')   // 'view' | 'edit'
  const [draft, setDraft] = useState(() => makeDraft(chart))
  const [saving, setSaving]     = useState(false)
  const [saveErr, setSaveErr]   = useState(null)
  const [dentition, setDentition] = useState('adult')
  const [popupTooth, setPopupTooth] = useState(null)
  const chartId = chart?.id
  const toast = useToast()

  // A new `chart` prop must not wipe UNSAVED edits. It changes when ANOTHER section
  // of this page saves (e.g. Vital Signs' own บันทึก → refreshChart), and resetting
  // here used to throw away a clinical note being typed (found 2026-10-18). While
  // the draft has edits it is kept; save() reads the other sections' data from the
  // fresh `chart` prop, so their new values are still written. A clean editor
  // (or one just saved — mode is back to 'view') resyncs as before.
  const dirtyRef = useRef(false)
  useEffect(() => { if (!dirtyRef.current) setDraft(makeDraft(chart)) }, [chart])

  // unsaved-changes flag → drives the page-level floating save bar
  const baseline = useMemo(() => JSON.stringify(makeDraft(chart)), [chart])
  const dirty = mode === 'edit' && JSON.stringify(draft) !== baseline
  dirtyRef.current = dirty

  const startEdit = () => { setMode('edit'); setSaveErr(null) }
  const cancel    = () => {
    setDraft(makeDraft(chart)); setMode('view'); setSaveErr(null); setPopupTooth(null)
  }

  function addFinding(tooth, type, detail) {
    setDraft((d) => ({
      ...d,
      entries: [...d.entries, { tag: tooth, type, detail }],
    }))
  }
  function addFullMouthFinding(detail) {
    setDraft((d) => ({
      ...d,
      entries: [...d.entries, { tag: FULL_MOUTH, type: '', detail }],
    }))
  }
  function updateFindingDetail(index, value) {
    setDraft((d) => {
      const arr = [...d.entries]
      arr[index] = { ...arr[index], detail: value }
      return { ...d, entries: arr }
    })
  }
  function removeFinding(index) {
    setDraft((d) => ({ ...d, entries: d.entries.filter((_, i) => i !== index) }))
  }

  // Returns true once saved (false on error) so the inline button and the page's
  // บันทึกทั้งหมด bar can confirm with a toast.
  async function save() {
    if (!chartId) { setSaveErr('ไม่พบ chart_id'); toast.error('ไม่พบ chart_id'); return false }
    setSaving(true); setSaveErr(null)
    try {
      const oral_exam = draft.entries.map((e) => {
        const out = {
          // Xano `oral_exam.tag` is a plain text field — store the single tooth
          // number (or 'Full Mouth') as a string.
          tag:    String(e.tag || ''),
          type:   e.type || '',
          detail: e.detail || '',
        }
        if (e.user_id != null)   out.user_id   = e.user_id
        if (e.timestamp != null) out.timestamp = e.timestamp
        if (e.date)              out.date      = e.date
        return out
      })
      const r = await fetch(`/api/charts/${chartId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ oral_exam }),
      })
      if (r.status === 401) { redirectToLogin(); return }
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${t || r.statusText}`)
      }
      setMode('view')
      onSaved?.()
      return true
    } catch (e) {
      const msg = e?.message || 'บันทึกไม่สำเร็จ'
      setSaveErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
      return false
    } finally {
      setSaving(false)
    }
  }

  // Map of tag → array of {entry, index} for fast popup lookup + chart highlighting.
  const findingsByTag = useMemo(() => {
    const m = new Map()
    draft.entries.forEach((entry, index) => {
      const key = String(entry.tag || '')
      if (!m.has(key)) m.set(key, [])
      m.get(key).push({ entry, index })
    })
    return m
  }, [draft.entries])

  const teethWithFindings = useMemo(() => {
    const arr = []
    for (const [tag] of findingsByTag) if (tag && tag !== FULL_MOUTH) arr.push(tag)
    return arr
  }, [findingsByTag])

  const fullMouthFindings = findingsByTag.get(FULL_MOUTH) || []
  const popupFindings     = popupTooth ? (findingsByTag.get(String(popupTooth)) || []) : []

  useChartSave('oral-exam', dirty, save, cancel)
  async function saveAndConfirm() {
    if (await save()) toast.success('บันทึกแล้ว')
  }

  const right = mode === 'view'
    ? (canEdit && chartId ? (
        <Button variant="soft" size="sm" icon={Pencil} onClick={startEdit}>แก้ไข</Button>
      ) : (!canEdit && chartId && lockNote ? (
        <Badge tone="amber" title={lockNote}><Lock size={11} strokeWidth={2.25} />ล็อกการแก้ไข</Badge>
      ) : null))
    : (
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={cancel} disabled={saving}>ยกเลิก</Button>
        <Button size="sm" onClick={saveAndConfirm} loading={saving}>บันทึก</Button>
      </div>
    )

  return (
    <section className="bg-white border border-slate-200 rounded-2xl">
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">
          <ScanEye size={14} strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Full Mouth Exam</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">
            สถานะปัจจุบันของฟันแต่ละซี่ {mode === 'edit' && <span className="text-amber-600 font-medium">· แก้ไขอยู่</span>}
          </div>
        </div>
        {right}
      </header>

      <div className="p-4 sm:p-5 space-y-4">
        {saveErr && <ErrorState title={saveErr} />}

        {mode === 'view' && !canEdit && chartId && lockNote && (
          <Banner tone="warn" icon={Lock}>{lockNote}</Banner>
        )}

        {mode === 'edit' ? (
          <>
            <DentitionPicker dentition={dentition} setDentition={setDentition} />

            <div className="-mx-5 px-5 overflow-x-auto no-scrollbar">
              <div className="text-[13px] text-slate-500 mb-3">คลิกฟันที่ต้องการบันทึก finding</div>
              <ToothChartArea
                dentition={dentition}
                selected={teethWithFindings}
                onToothClick={(num) => setPopupTooth(String(num))}
              />
            </div>

            <PerToothFindingsList
              findingsByTag={findingsByTag}
              onUpdateDetail={updateFindingDetail}
              onRemove={removeFinding}
              onToothClick={(num) => setPopupTooth(num)}
            />

            <FullMouthSection
              findings={fullMouthFindings}
              onAdd={addFullMouthFinding}
              onUpdateDetail={updateFindingDetail}
              onRemove={removeFinding}
            />
          </>
        ) : (
          <OralExamChart findings={draft.entries} framed={false} />
        )}
      </div>

      {popupTooth && (
        <ToothFindingPopup
          tooth={popupTooth}
          findings={popupFindings}
          onAdd={(type, detail) => addFinding(popupTooth, type, detail)}
          onUpdateDetail={updateFindingDetail}
          onRemove={removeFinding}
          onClose={() => setPopupTooth(null)}
        />
      )}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Tooth chart area                                                            */
/* -------------------------------------------------------------------------- */

function DentitionPicker({ dentition, setDentition }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <SectionLabel>Dentition</SectionLabel>
      <Segmented
        size="sm"
        value={dentition}
        onChange={setDentition}
        options={[
          { value: 'adult', label: 'Adult' },
          { value: 'pedo',  label: 'Pediatric' },
          { value: 'mixed', label: 'Mixed' },
        ]}
      />
    </div>
  )
}

function ToothChartArea({ dentition, selected, onToothClick }) {
  const showMixedLabels = dentition === 'mixed'
  return (
    <div className="space-y-5">
      {(dentition === 'adult' || dentition === 'mixed') && (
        <div>
          {showMixedLabels && (
            <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Adult · permanent</div>
          )}
          <AdultToothChart selected={selected} onToggle={onToothClick} />
        </div>
      )}
      {showMixedLabels && (
        <div className="border-t border-dashed border-slate-200" />
      )}
      {(dentition === 'pedo' || dentition === 'mixed') && (
        <div>
          {showMixedLabels && (
            <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Pediatric · primary</div>
          )}
          <PedoToothChart selected={selected} onToggle={onToothClick} />
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Per-tooth findings list (edit mode review)                                  */
/* -------------------------------------------------------------------------- */

function PerToothFindingsList({ findingsByTag, onUpdateDetail, onRemove, onToothClick }) {
  const teeth = []
  for (const [tag, list] of findingsByTag) {
    if (tag && tag !== FULL_MOUTH) teeth.push({ tooth: tag, list })
  }
  teeth.sort((a, b) => sortTeeth(a.tooth, b.tooth))

  if (teeth.length === 0) return null

  const totalCount = teeth.reduce((acc, t) => acc + t.list.length, 0)

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-3.5 py-2 bg-slate-50/60 border-b border-slate-100 flex items-center gap-2">
        <SectionLabel>Findings ตามซี่ฟัน</SectionLabel>
        <span className="text-[12.5px] text-slate-400">· {totalCount} finding</span>
      </div>
      <div className="divide-y divide-slate-100">
        {teeth.map(({ tooth, list }) => (
          <div key={tooth} className="px-3.5 py-2.5 flex items-start gap-3">
            <button type="button" onClick={() => onToothClick(tooth)}
              className="inline-flex items-center justify-center min-w-[36px] h-[26px] px-2 rounded-md bg-brand-50 ring-1 ring-brand-200 text-[14px] font-mono font-bold text-brand-700 hover:bg-brand-100 transition-colors"
              title="เปิด popup เพื่อแก้ไข">
              {tooth}
            </button>
            <div className="flex-1 min-w-0 space-y-1">
              {list.map(({ entry, index }) => (
                <div key={index} className="flex items-center gap-2">
                  <TypeChip type={entry.type} />
                  <input
                    type="text"
                    value={entry.detail || ''}
                    onChange={(e) => onUpdateDetail(index, e.target.value)}
                    placeholder="(แก้ไข detail)"
                    className="flex-1 text-[14px] text-slate-600 bg-transparent border-none outline-none focus:bg-white focus:rounded px-1 py-0.5 placeholder:text-slate-300 placeholder:italic"
                  />
                  <IconButton icon={Trash2} size="sm" variant="danger-soft" onClick={() => onRemove(index)} label="ลบ finding" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Full Mouth section                                                          */
/* -------------------------------------------------------------------------- */

function FullMouthSection({ findings, onAdd, onUpdateDetail, onRemove }) {
  const [detail, setDetail] = useState('')

  function handleAdd() {
    const v = detail.trim()
    if (!v) return
    onAdd(v)
    setDetail('')
  }

  return (
    <div className="rounded-xl border border-brand-100 bg-gradient-to-br from-brand-50/40 to-white p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white ring-1 ring-brand-200 text-brand-600">
          <ScanEye size={12} strokeWidth={2.25} />
        </span>
        <div className="flex-1">
          <div className="text-[13.5px] uppercase tracking-[0.12em] font-semibold text-brand-700">Full Mouth · ทั้งปาก</div>
          <div className="text-[12.5px] text-slate-500 -mt-0.5">สำหรับสภาวะที่เกี่ยวข้องโดยรวม เช่น หินปูนหนัก · กลิ่นปาก · generalized gingivitis</div>
        </div>
        {findings.length > 0 && <span className="text-[12.5px] text-slate-400">{findings.length} finding</span>}
      </div>

      {findings.length > 0 && (
        <div className="space-y-1">
          {findings.map(({ entry, index }) => (
            <div key={index} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white ring-1 ring-slate-200">
              <input
                type="text"
                value={entry.detail || ''}
                onChange={(e) => onUpdateDetail(index, e.target.value)}
                placeholder="(ระบุสภาวะ)"
                className="flex-1 text-[14px] text-slate-700 bg-transparent border-none outline-none focus:bg-slate-50 focus:rounded px-1 py-0.5"
              />
              <IconButton icon={Trash2} size="sm" variant="danger-soft" onClick={() => onRemove(index)} label="ลบ" />
            </div>
          ))}
        </div>
      )}

      <div className="pt-3 border-t border-brand-100/60 flex items-end gap-2">
        <Field label="เพิ่มสภาวะใหม่" className="flex-1">
          <Input
            type="text"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd() } }}
            placeholder="เช่น หินปูนหนัก, generalized gingivitis, กลิ่นปาก"
          />
        </Field>
        <Button variant="secondary" icon={Plus} onClick={handleAdd} disabled={!detail.trim()}>เพิ่ม</Button>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Tooth-finding popup                                                         */
/* -------------------------------------------------------------------------- */

function ToothFindingPopup({ tooth, findings, onAdd, onUpdateDetail, onRemove, onClose }) {
  const [pickedType, setPickedType] = useState('')
  const [detail, setDetail] = useState('')

  const usedTypes = useMemo(() => new Set(findings.map(({ entry }) => entry.type)), [findings])
  const allTypesUsed = ORAL_EXAM_TYPES.every((t) => usedTypes.has(t))

  function handleAdd() {
    if (!pickedType) return
    if (usedTypes.has(pickedType)) return // safety net
    onAdd(pickedType, detail.trim())
    setPickedType('')
    setDetail('')
    onClose?.() // auto-close the popup after adding a finding
  }

  return (
    <Modal
      onClose={onClose}
      title={`ซี่ฟัน ${tooth}`}
      subtitle={findings.length > 0 ? `${findings.length} finding บนซี่นี้` : 'ยังไม่มี finding บนซี่นี้'}
      footer={(
        <DrawerFooter
          onCancel={onClose}
          cancelLabel="ปิด"
          onSave={allTypesUsed ? undefined : handleAdd}
          saveLabel="เพิ่ม finding"
          canSave={!!pickedType}
        />
      )}
    >
      {/* Existing findings — detail is inline-editable */}
      {findings.length > 0 && (
        <div className="space-y-1.5 mb-4">
          {findings.map(({ entry, index }) => (
            <div key={index} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50 ring-1 ring-slate-100">
              <TypeChip type={entry.type} />
              <input
                type="text"
                value={entry.detail || ''}
                onChange={(e) => onUpdateDetail(index, e.target.value)}
                placeholder="(แก้ไข detail)"
                className="flex-1 text-[14px] text-slate-700 bg-transparent border-none outline-none focus:bg-white focus:rounded px-1 py-0.5 placeholder:text-slate-300 placeholder:italic"
              />
              <IconButton icon={Trash2} size="sm" variant="danger-soft" onClick={() => onRemove(index)} label="ลบ" />
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {allTypesUsed ? (
        <div className="px-3 py-3 rounded-lg bg-slate-50 text-[14px] text-slate-500 text-center">
          มี finding ครบทุก type สำหรับซี่นี้แล้ว
        </div>
      ) : (
        <div className={`space-y-3 ${findings.length > 0 ? 'pt-3 border-t border-slate-100' : ''}`}>
          <Field label="เลือก Type (ที่ยังไม่มี)" required>
            <ChoiceChips
              allowNone
              options={ORAL_EXAM_TYPES.map((t) => ({ value: t, label: t, color: typeColor(t), disabled: usedTypes.has(t) }))}
              value={pickedType}
              onChange={setPickedType}
            />
          </Field>
          <Field label="Detail" hint="เลือกใส่">
            <Input
              type="text"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd() } }}
              placeholder="เช่น occlusal caries, symptomatic"
            />
          </Field>
        </div>
      )}
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                       */
/* -------------------------------------------------------------------------- */

function TypeChip({ type }) {
  return <Badge color={typeColor(type)} dot>{type || '—'}</Badge>
}

function SectionLabel({ children }) {
  return (
    <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500">{children}</div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function makeDraft(chart) {
  const raw = Array.isArray(chart?.oral_exam) ? chart.oral_exam : []
  return {
    entries: raw.map((e) => ({
      tag:       normalizeTag(e?.tag),
      type:      e?.type || '',
      detail:    e?.detail || '',
      user_id:   e?.user_id ?? null,
      date:      e?.date ?? '',
      timestamp: e?.timestamp ?? null,
    })),
  }
}

// Accept either a string or array-of-strings; collapse to a single tooth tag.
function normalizeTag(t) {
  if (Array.isArray(t)) return String(t[0] || '')
  return String(t || '')
}

// Sort FDI tooth numbers in clinically natural order; Full Mouth sinks to end.
function sortTeeth(a, b) {
  if (a === FULL_MOUTH) return 1
  if (b === FULL_MOUTH) return -1
  const na = Number(a), nb = Number(b)
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb
  return String(a).localeCompare(String(b))
}
